import * as Sentry from '@sentry/react';

// Helper to get env var from either process.env or import.meta.env
const getEnv = (key: string): string | undefined => {
  // Check for Vite environment (import.meta.env)
  if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
    const viteEnv = (import.meta as any).env;
    return viteEnv[`VITE_${key}`] || viteEnv[key];
  }
  // Check for process.env (Node.js or webpack)
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key];
  }
  return undefined;
};

// Check if we're in development mode
const isDevelopment = () => {
  const mode = getEnv('MODE') || getEnv('NODE_ENV');
  return mode === 'development' || mode === 'dev';
};

export const initSentry = () => {
  const sentryDsn = getEnv('SENTRY_DSN') || getEnv('REACT_APP_SENTRY_DSN');
  
  // Skip Sentry initialization if no DSN is provided
  if (!sentryDsn) {
    console.log('Sentry DSN not found, skipping initialization');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: isDevelopment() ? 1.0 : 0.1,
    environment: getEnv('NODE_ENV') || getEnv('MODE') || 'development',
    beforeSend(event: any) {
      // Filter out events in development if needed
      if (isDevelopment()) {
        console.log('Sentry event:', event);
      }
      return event;
    },
  });

  // Global error handler for unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    Sentry.captureException(event.reason);
  });

  // Global error handler for JavaScript errors
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    Sentry.captureException(event.error);
  });

  // Override console.error to automatically capture errors
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // Call the original console.error
    originalConsoleError.apply(console, args);
    
    // Check if any argument is an Error object and capture it
    args.forEach(arg => {
      if (arg instanceof Error) {
        Sentry.captureException(arg);
      } else if (typeof arg === 'string' && arg.toLowerCase().includes('error')) {
        // Capture string error messages
        Sentry.captureMessage(arg, 'error');
      }
    });
  };
};

export const captureError = (error: Error, context?: Record<string, any>) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    Sentry.captureException(error);
  });
};

export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  Sentry.captureMessage(message, level);
};
